/**
 * 纯几何：形状间最短距离 / 点到多边形距离。
 *
 * 约定：所有距离单位均为像素；接触 = 0，相交 < 0，远离 > 0。
 * 这套约定让 collision 模块判定 < -minGapPx 时即为冲突。
 */

/**
 * 圆 ↔ 圆 最短距离。
 * @param {{cx:number,cy:number,r:number}} c1
 * @param {{cx:number,cy:number,r:number}} c2
 * @returns {number} 距离（可负）
 *
 * @example
 * circleToCircle({cx:0,cy:0,r:5}, {cx:10,cy:0,r:3}) // => 2
 */
export function circleToCircle(c1, c2) {
  const dx = c2.cx - c1.cx;
  const dy = c2.cy - c1.cy;
  return Math.sqrt(dx * dx + dy * dy) - c1.r - c2.r;
}

/**
 * 轴对齐矩形 ↔ 轴对齐矩形 最短间隙（AABB）。
 * 两矩形相交时返回 0（不细分穿透深度，对冲突判定够用）。
 * @param {{x:number,y:number,w:number,h:number}} r1 - 左上角 + 宽高
 * @param {{x:number,y:number,w:number,h:number}} r2
 * @returns {number}
 *
 * @example
 * rectToRect({x:0,y:0,w:10,h:10}, {x:20,y:0,w:5,h:5}) // => 10
 */
export function rectToRect(r1, r2) {
  const dx = Math.max(0, Math.max(r1.x - (r2.x + r2.w), r2.x - (r1.x + r1.w)));
  const dy = Math.max(0, Math.max(r1.y - (r2.y + r2.h), r2.y - (r1.y + r1.h)));
  if (dx === 0 && dy === 0) {
    // AABB 相交，返回负值（用穿透深度的近似——取重叠长度的较小者，便于 collision 判 < -minGapPx）
    const overlapX = Math.min(r1.x + r1.w, r2.x + r2.w) - Math.max(r1.x, r2.x);
    const overlapY = Math.min(r1.y + r1.h, r2.y + r2.h) - Math.max(r1.y, r2.y);
    return -Math.min(overlapX, overlapY);
  }
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 圆 ↔ 轴对齐矩形 最短距离。
 * @param {{cx:number,cy:number,r:number}} c
 * @param {{x:number,y:number,w:number,h:number}} r
 * @returns {number}
 *
 * @example
 * circleToRect({cx:20,cy:5,r:3}, {x:0,y:0,w:10,h:10}) // => 7
 */
export function circleToRect(c, r) {
  const closestX = Math.max(r.x, Math.min(c.cx, r.x + r.w));
  const closestY = Math.max(r.y, Math.min(c.cy, r.y + r.h));
  const dx = c.cx - closestX;
  const dy = c.cy - closestY;
  return Math.sqrt(dx * dx + dy * dy) - c.r;
}

/**
 * 点到多段线（点数组连成的折线）的最小距离。
 * 用于判断"圆心到轮廓的最短距离"（type.clampSelf 中保证圆与轮廓相切）。
 * @param {{x:number,y:number}} pt
 * @param {Array<Array<number>>} points - 折线点集 [[x,y],...]
 * @returns {number}
 */
export function pointToPolylineMinDistance(pt, points) {
  if (!points || points.length === 0) return Infinity;
  let min = Infinity;
  for (let i = 0; i < points.length; i++) {
    const dx = points[i][0] - pt.x;
    const dy = points[i][1] - pt.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d < min) min = d;
  }
  return min;
}

/**
 * 点到旋转矩形的最短距离（世界坐标点 → 矩形局部坐标 → AABB 最近点）。
 * @param {{x:number,y:number}} pt - 世界坐标点
 * @param {{cx:number,cy:number,w:number,h:number,rotationRad:number}} rect
 * @returns {number}
 */
function pointToOrientedRect(pt, rect) {
  const dx = pt.x - rect.cx;
  const dy = pt.y - rect.cy;
  const cosR = Math.cos(-rect.rotationRad);
  const sinR = Math.sin(-rect.rotationRad);
  const lx = dx * cosR - dy * sinR;
  const ly = dx * sinR + dy * cosR;
  const hw = rect.w / 2;
  const hh = rect.h / 2;
  const clampX = Math.max(-hw, Math.min(lx, hw));
  const clampY = Math.max(-hh, Math.min(ly, hh));
  return Math.sqrt(
    (lx - clampX) * (lx - clampX) + (ly - clampY) * (ly - clampY)
  );
}

/**
 * 圆 ↔ 旋转矩形 最短距离。
 * 圆心变换到矩形局部坐标系 → AABB 最近点距离 → 减去半径。
 * @param {{cx:number,cy:number,r:number}} c
 * @param {{cx:number,cy:number,w:number,h:number,rotationRad:number}} rect
 * @returns {number}
 */
export function circleToOrientedRect(c, rect) {
  return pointToOrientedRect({ x: c.cx, y: c.cy }, rect) - c.r;
}

/**
 * 旋转矩形 OBB 投影到轴上的半宽。
 * @param {number} hw - 半宽
 * @param {number} hh - 半高
 * @param {number} cosT - cos(rotationRad)
 * @param {number} sinT - sin(rotationRad)
 * @param {{x:number,y:number}} axis - 投影轴（单位向量）
 * @returns {number}
 */
function obbHalfExtent(hw, hh, cosT, sinT, axis) {
  return (
    Math.abs(hw * (cosT * axis.x + sinT * axis.y)) +
    Math.abs(hh * (-sinT * axis.x + cosT * axis.y))
  );
}

/**
 * SAT 判定两旋转矩形是否相交，并返回最大穿透深度（用于相交时）。
 * @returns {{overlap:boolean,maxPenetration:number}}
 */
function satOBBs(a, b) {
  const cosA = Math.cos(a.rotationRad);
  const sinA = Math.sin(a.rotationRad);
  const cosB = Math.cos(b.rotationRad);
  const sinB = Math.sin(b.rotationRad);
  const hwA = a.w / 2;
  const hhA = a.h / 2;
  const hwB = b.w / 2;
  const hhB = b.h / 2;

  const axes = [
    { x: cosA, y: sinA },
    { x: -sinA, y: cosA },
    { x: cosB, y: sinB },
    { x: -sinB, y: cosB }
  ];

  let minGap = Infinity;

  for (var i = 0; i < axes.length; i++) {
    const axis = axes[i];
    const projA_c = a.cx * axis.x + a.cy * axis.y;
    const projA_e = obbHalfExtent(hwA, hhA, cosA, sinA, axis);
    const projB_c = b.cx * axis.x + b.cy * axis.y;
    const projB_e = obbHalfExtent(hwB, hhB, cosB, sinB, axis);

    const aMin = projA_c - projA_e;
    const aMax = projA_c + projA_e;
    const bMin = projB_c - projB_e;
    const bMax = projB_c + projB_e;

    const gap = Math.max(aMin, bMin) - Math.min(aMax, bMax);
    // gap > 0 → 该轴分离
    if (gap > 0) {
      return { overlap: false, maxPenetration: 0 };
    }
    // gap ≤ 0 → 该轴重叠，记录重叠量
    if (gap > minGap) minGap = gap;
  }

  // 所有轴都重叠 → 相交，maxPenetration = 各轴最小分离距离的绝对值
  return { overlap: true, maxPenetration: Math.abs(minGap) };
}

/**
 * 旋转矩形顶点到另一旋转矩形的最短距离。
 */
function vertexMinDist(a, b) {
  var verticesA = getOrientedCorners(a);
  var verticesB = getOrientedCorners(b);
  var minDist = Infinity;
  for (var i = 0; i < verticesA.length; i++) {
    var d = pointToOrientedRect(verticesA[i], b);
    if (d < minDist) minDist = d;
  }
  for (var j = 0; j < verticesB.length; j++) {
    var distB = pointToOrientedRect(verticesB[j], a);
    if (distB < minDist) minDist = distB;
  }
  return minDist;
}

/**
 * 取旋转矩形的 4 个角点（世界坐标）。
 * @returns {Array<{x:number,y:number}>}
 */
function getOrientedCorners(rect) {
  var cosT = Math.cos(rect.rotationRad);
  var sinT = Math.sin(rect.rotationRad);
  var hw = rect.w / 2;
  var hh = rect.h / 2;
  var local = [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh }
  ];
  for (var i = 0; i < local.length; i++) {
    var p = local[i];
    local[i] = {
      x: rect.cx + p.x * cosT - p.y * sinT,
      y: rect.cy + p.x * sinT + p.y * cosT
    };
  }
  return local;
}

/**
 * 旋转矩形 ↔ 旋转矩形 最短距离。
 * SAT 判相交 → 分离时用 8 个顶点到对方矩形的最短距离，相交时返回负穿透深度。
 * @param {{cx:number,cy:number,w:number,h:number,rotationRad:number}} a
 * @param {{cx:number,cy:number,w:number,h:number,rotationRad:number}} b
 * @returns {number}
 */
export function orientedRectToOrientedRect(a, b) {
  var satResult = satOBBs(a, b);
  if (satResult.overlap) {
    return -satResult.maxPenetration;
  }
  return vertexMinDist(a, b);
}

/**
 * 通用形状距离分发（按 shape kind 调对应几何函数）。
 * @param {Object} s1 - { kind:'circle'|'rect'|'orientedRect', ...几何字段 }
 * @param {Object} s2
 * @returns {number}
 */
export function shapeDistance(s1, s2) {
  if (s1.kind === 'circle' && s2.kind === 'circle') {
    return circleToCircle(s1, s2);
  }
  if (s1.kind === 'rect' && s2.kind === 'rect') {
    return rectToRect(s1, s2);
  }
  if (s1.kind === 'circle' && s2.kind === 'rect') {
    return circleToRect(s1, s2);
  }
  if (s1.kind === 'rect' && s2.kind === 'circle') {
    return circleToRect(s2, s1);
  }
  // 旋转矩形 vs 圆
  if (s1.kind === 'orientedRect' && s2.kind === 'circle') {
    return circleToOrientedRect(s2, s1);
  }
  if (s1.kind === 'circle' && s2.kind === 'orientedRect') {
    return circleToOrientedRect(s1, s2);
  }
  // 旋转矩形 vs 旋转矩形
  if (s1.kind === 'orientedRect' && s2.kind === 'orientedRect') {
    return orientedRectToOrientedRect(s1, s2);
  }
  // 旋转矩形 vs AABB（AABB 是 rotationRad=0 的特例）
  if (s1.kind === 'orientedRect' && s2.kind === 'rect') {
    return orientedRectToOrientedRect(s1, {
      cx: s2.x + s2.w / 2,
      cy: s2.y + s2.h / 2,
      w: s2.w,
      h: s2.h,
      rotationRad: 0
    });
  }
  if (s1.kind === 'rect' && s2.kind === 'orientedRect') {
    return orientedRectToOrientedRect(
      {
        cx: s1.x + s1.w / 2,
        cy: s1.y + s1.h / 2,
        w: s1.w,
        h: s1.h,
        rotationRad: 0
      },
      s2
    );
  }
  return Infinity;
}
